export const showToast = (message) => {
  // Remove any existing toasts first to avoid stacking
  const existingToasts = document.querySelectorAll('.custom-toast');
  existingToasts.forEach((toast) => toast.remove());

  const toast = document.createElement('div');
  toast.className = 'custom-toast';
  toast.style.cssText = `
    position: fixed; top: 20px; right: 20px; 
    background: #fef2f2; color: #991b1b; padding: 16px 24px; 
    border-radius: 12px; box-shadow: 0 10px 40px rgba(0,0,0,0.1); 
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
    font-weight: 500; z-index: 10000; max-width: 400px;
    border: 2px solid #ef4444; animation: slideIn 0.3s ease;
    cursor: pointer;
  `;

  // Add slide animation if not already added
  if (!document.head.querySelector('#toast-styles')) {
    const style = document.createElement('style');
    style.id = 'toast-styles';
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes slideOut {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(100%); opacity: 0; }
      }
    `;
    document.head.appendChild(style);
  }

  // Use innerHTML if message contains HTML tags, otherwise use textContent for safety
  if (message.includes('<') && message.includes('>')) {
    toast.innerHTML = message;
  } else {
    toast.textContent = message;
  }

  // Click to dismiss
  toast.addEventListener('click', () => {
    toast.style.animation = 'slideOut 0.3s ease';
    setTimeout(() => {
      if (document.body.contains(toast)) {
        document.body.removeChild(toast);
      }
    }, 300);
  });

  document.body.appendChild(toast);

  // Auto remove after 5 seconds
  setTimeout(() => {
    if (document.body.contains(toast)) {
      toast.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => {
        if (document.body.contains(toast)) {
          document.body.removeChild(toast);
        }
      }, 300);
    }
  }, 5000);
};

const BANNER_CONTAINER_ID = 'comprehendpt-banner-container';
const BANNER_TOAST_PREFIX = 'comprehendpt-banner-toast-';

let _bannerContainer = null;

function getBannerContainer() {
  if (_bannerContainer) return _bannerContainer;
  const existing = document.getElementById(BANNER_CONTAINER_ID);
  if (existing) {
    _bannerContainer = existing;
    return existing;
  }
  _bannerContainer = document.createElement('div');
  _bannerContainer.id = BANNER_CONTAINER_ID;
  _bannerContainer.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    width: 25%;
    min-width: 280px;
    max-width: 420px;
    z-index: 2147483647;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
    font-size: 14px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 10px;
  `;
  if (document.body) {
    document.body.appendChild(_bannerContainer);
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      if (_bannerContainer && !_bannerContainer.parentNode) {
        document.body.appendChild(_bannerContainer);
      }
    });
  }
  return _bannerContainer;
}

/**
 * Show a toast-style notification (top-right, ~25% width, rounded) that stays until the user closes it.
 * Multiple toasts stack when each call uses a different toastId.
 * @param {Object} options
 * @param {string} options.content - HTML string for toast body (can include links)
 * @param {string} [options.toastId] - Unique id for this toast. Use different ids to stack multiple toasts; omit for single-toast behavior.
 * @param {string} [options.logoUrl] - Optional URL for a logo image shown to the left of the content (e.g. chrome.runtime.getURL('Logo.svg'))
 * @param {() => void} [options.onDismiss] - Called when the user closes the toast (use to persist dismissal state)
 * @returns {boolean} - true if toast was shown, false if skipped
 */
export function showBanner({ content, toastId, logoUrl, onDismiss }) {
  if (typeof document === 'undefined') return false;
  const id = toastId != null ? String(toastId) : 'default';
  const rootId = BANNER_TOAST_PREFIX + id;
  const container = getBannerContainer();
  if (container.querySelector('#' + rootId)) {
    return false;
  }

  if (!document.head.querySelector('#toast-styles')) {
    const style = document.createElement('style');
    style.id = 'toast-styles';
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes slideOut {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(100%); opacity: 0; }
      }
    `;
    document.head.appendChild(style);
  }

  const root = document.createElement('div');
  root.id = rootId;
  root.style.cssText = `
    animation: slideIn 0.3s ease;
  `;

  const toast = document.createElement('div');
  toast.style.cssText = `
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 20px;
    background: #fff;
    color: #1a202c;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 10px 40px rgba(0,0,0,0.12);
  `;

  const textWrap = document.createElement('span');
  textWrap.style.cssText = 'flex: 1; line-height: 1.4;';
  textWrap.innerHTML = content;

  if (logoUrl) {
    const logo = document.createElement('img');
    logo.src = logoUrl;
    logo.alt = 'Comprehend';
    logo.style.cssText =
      'width: 28px; height: 28px; flex-shrink: 0; object-fit: contain;';
    toast.appendChild(logo);
  }

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.textContent = '×';
  closeBtn.style.cssText = `
    background: transparent;
    border: none;
    color: #718096;
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
    padding: 0 4px;
    flex-shrink: 0;
  `;
  closeBtn.addEventListener('click', () => {
    try {
      if (onDismiss) onDismiss();
    } catch (e) {
      console.warn('Comprehend banner dismiss error:', e);
    }
    root.style.animation = 'slideOut 0.3s ease';
    setTimeout(() => {
      if (root.parentNode) {
        root.parentNode.removeChild(root);
      }
    }, 300);
  });

  toast.appendChild(textWrap);
  toast.appendChild(closeBtn);
  root.appendChild(toast);
  container.appendChild(root);

  return true;
}
